/**
 * A way for non-React code to ask the lifecycle provider for one fresh read of
 * `GET /v2/trading-session`.
 *
 * The store holds a COPY of the server's answer, and for an OAuth-backed
 * authority that answer carries the provider token's own expiry (about an
 * hour). The server refreshes that token silently, so the copy goes stale long
 * before the session does: acting on the stale copy is how a live account gets
 * told to sign for authority it already holds.
 *
 * The provider owns the fetch (it also re-exchanges the app token), so it
 * registers its refresh here instead of this module duplicating any of it.
 */

type TradingSessionRefresher = () => Promise<void>;

/** How long a caller waits for the refresh before deciding without it. */
export const STATUS_REFRESH_TIMEOUT_MS = 5_000;

let refresher: TradingSessionRefresher | null = null;
let inFlight: Promise<void> | null = null;

export function setTradingSessionRefresher(
    next: TradingSessionRefresher | null,
): void {
    refresher = next;
    if (next === null) inFlight = null;
}

async function withTimeout(
    promise: Promise<void>,
    timeoutMs: number,
): Promise<void> {
    if (timeoutMs <= 0) return promise;
    return new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, timeoutMs);
        void promise.then(() => {
            clearTimeout(timer);
            resolve();
        });
    });
}

/**
 * Refreshes the trading-session status once, coalescing concurrent callers, and
 * gives up after `timeoutMs` so a slow network cannot hold a user action open.
 * It never rejects: the caller re-reads the store and decides from what is
 * there, which is the same fail-closed answer it would have had anyway.
 */
export async function refreshTradingSessionStatus(
    timeoutMs = STATUS_REFRESH_TIMEOUT_MS,
): Promise<void> {
    const current = refresher;
    if (!current) return;

    if (!inFlight) {
        const run = Promise.resolve()
            .then(async () => current())
            .catch(() => undefined);
        inFlight = run;
        void run.then(() => {
            if (inFlight === run) inFlight = null;
        });
    }

    return withTimeout(inFlight, timeoutMs);
}
